import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './Button';
import { useLanguage } from '../../context/LanguageContext';
const slides = [{
  id: 1,
  image: 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=1920&q=80',
  key: 'slide1'
}, {
  id: 2,
  image: 'https://images.unsplash.com/photo-1551190822-a9ce113ac100?w=1920&q=80',
  key: 'slide2'
}, {
  id: 3,
  image: 'https://images.unsplash.com/photo-1631815588090-d4bfec5b1ccb?w=1920&q=80',
  key: 'slide3'
}, {
  id: 4,
  image: 'https://images.unsplash.com/photo-1538108149393-fbbd81895907?w=1920&q=80',
  key: 'slide4'
}, {
  id: 5,
  image: 'https://images.unsplash.com/photo-1504439468489-c8920d796a29?w=1920&q=80',
  key: 'slide5'
}];
export function HeroCarousel() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const {
    t
  } = useLanguage();
  const nextSlide = useCallback(() => {
    setCurrentSlide(prev => (prev + 1) % slides.length);
  }, []);
  const prevSlide = () => {
    setCurrentSlide(prev => (prev - 1 + slides.length) % slides.length);
  };
  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(nextSlide, 7000);
    return () => clearInterval(interval);
  }, [isPaused, nextSlide]);
  return <div className="relative h-screen w-full overflow-hidden bg-caces-navy" onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)} role="region" aria-label="Hero Carousel" data-id="element-157">
      {/* Slides */}
      {slides.map((slide, index) => {
      // @ts-ignore - dynamic key access
      const content = t.hero[slide.key];
      const isActive = index === currentSlide;
      return <div key={slide.id} className={`
              absolute inset-0 transition-opacity duration-1000 ease-in-out
              ${isActive ? 'opacity-100 z-10' : 'opacity-0 z-0'}
            `} aria-hidden={!isActive} data-id="element-158">
            {/* Background Image */}
            <img src={slide.image} alt="" className="absolute inset-0 w-full h-full object-cover" data-id="element-159" />

            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-caces-navy/90 via-caces-navy/50 to-transparent" data-id="element-160" />

            {/* Content */}
            <div className="absolute inset-0 flex items-center justify-center" data-id="element-161">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white mt-16" data-id="element-162">
                {slide.id === 1 && <h2 className="text-caces-accent font-medium tracking-widest uppercase mb-4 animate-in fade-in slide-in-from-bottom-4 duration-700" data-id="element-163">
                    Centro Ambulatorio de Cirugía Escalón
                  </h2>}

                <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight drop-shadow-lg" data-id="element-164">
                  {content.title}
                </h1>

                <p className="text-lg md:text-xl text-caces-light max-w-2xl mx-auto mb-10 drop-shadow-md" data-id="element-165">
                  {content.subtitle}
                </p>

                {/* Persistent Buttons (only visible on Slide 1 for visual hierarchy, or all if desired - sticking to slide 1 for main CTA focus as per design patterns, but prompt implies persistent. Let's make them persistent but only render once to avoid duplication in DOM? No, they need to be inside the slide to fade with it OR outside. Prompt says "persistent across all slides". Let's put them OUTSIDE the map loop.) */}
              </div>
            </div>
          </div>;
    })}

      {/* Persistent CTAs Overlay */}
      <div className="absolute inset-0 z-20 flex items-end justify-center pb-32 pointer-events-none" data-id="element-166">
        <div className="flex flex-col sm:flex-row gap-4 pointer-events-auto" data-id="element-167">
          <Link to="/patients" data-id="element-168">
            <Button size="lg" variant="white" className="min-w-[200px] shadow-lg" data-id="element-169">
              {t.hero.slide1.btnPatient}
            </Button>
          </Link>
          <Link to="/specialists" data-id="element-170">
            <Button size="lg" variant="outline" className="min-w-[200px] border-white text-white hover:bg-white/10 hover:text-white shadow-lg backdrop-blur-sm" data-id="element-171">
              {t.hero.slide1.btnSpecialist}
            </Button>
          </Link>
        </div>
      </div>

      {/* Controls */}
      <button onClick={prevSlide} className="absolute left-4 top-1/2 -translate-y-1/2 z-30 p-2 rounded-full bg-black/20 text-white hover:bg-black/40 transition-colors backdrop-blur-sm" aria-label="Previous slide" data-id="element-172">
        <ChevronLeft size={32} data-id="element-173" />
      </button>

      <button onClick={nextSlide} className="absolute right-4 top-1/2 -translate-y-1/2 z-30 p-2 rounded-full bg-black/20 text-white hover:bg-black/40 transition-colors backdrop-blur-sm" aria-label="Next slide" data-id="element-174">
        <ChevronRight size={32} data-id="element-175" />
      </button>

      {/* Indicators */}
      <div className="absolute bottom-12 left-1/2 -translate-x-1/2 z-30 flex gap-3" data-id="element-176">
        {slides.map((_, index) => <button key={index} onClick={() => setCurrentSlide(index)} className={`
              w-3 h-3 rounded-full transition-all duration-300
              ${index === currentSlide ? 'bg-white scale-110' : 'bg-white/40 hover:bg-white/60'}
            `} aria-label={`Go to slide ${index + 1}`} aria-current={index === currentSlide} data-id="element-177" />)}
      </div>
    </div>;
}